import { useState } from 'react'
import { Dialog, DialogBody, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@cutilei/ui'
import AuthService from 'services/auth'
import cutileiApi from 'services/cutileiApi'
import { isViewOnlyArea } from 'services/areaCodes'
import { getApiErrorMessage, logApiError } from 'services/apiError'
import CheckBox from 'components/CheckBox'
import MainButton from 'components/Buttons/MainButton'
import SecondaryButton from 'components/Buttons/SecondaryButton'
import ImportPermissionsModal from '../ImportPermissionsModal'
import AlertDialog from 'components/AlertDialog'
import submitOnEnter from 'utils/submitOnEnter'
import { FaFileImport } from 'react-icons/fa'
import { Formik } from 'formik'

import {
  Form,
  PermissionsContainer,
  CheckRow,
  Label,
  SubTitle,
  ErrorContainer
} from './styles'

export default function ProfessionalPermissionsModal ({
  visible,
  professional = {},
  accessLevels = [],
  permissions = [],
  professionalPermissions = [],
  onConfirm: handleConfirm,
  onClose: handleClose
}) {
  const [showImportPermissionsModal, setShowImportPermissionsModal] = useState (false)
  const [showConfirmDialog, setShowConfirmDialog] = useState (false)
  const [errorMessage, setErrorMessage] = useState (null)
  const businessId = AuthService.getBusinessId ()
  const token = AuthService.getToken ()
  
  const requestConfig = {
    headers: {
      'Authorization': `Bearer ${token}`
    }
  }

  const toggleImportPermissionsModal = () => {
    setShowImportPermissionsModal (!showImportPermissionsModal)
  }
  const toggleConfirmDialog = () => setShowConfirmDialog (!showConfirmDialog)

  return (
    <Dialog open={visible} onOpenChange={open => { if (!open) handleClose() }}>
      <DialogContent size='md' closeOnOutsideClick={false} aria-describedby={undefined}>
        <DialogHeader>
          <DialogTitle>Permissões de {professional.nickname}</DialogTitle>
        </DialogHeader>
        <Formik
          enableReinitialize={true}
          initialValues={{
            'permissions': permissions.map (permission => {
              const existingPermission = professionalPermissions.find (p => p.id === permission.id)
              return existingPermission
                ? { ...existingPermission, selected: true }
                : { ...permission, selected: false }
            })
          }}
          onSubmit={async (values, { setSubmitting }) => {
            try {
              const { permissions } = values

              const { data: editedPermissions } = await cutileiApi.put (
                `/businesses/${businessId}/professionals/${professional.id}/permissions`,
                { permissions },
                requestConfig
              )

              setSubmitting (false)
              handleConfirm (editedPermissions)
              handleClose ()
            } catch (error) {
              logApiError (error)
              setErrorMessage (getApiErrorMessage (error))
              setSubmitting (false)
            }
          }}
        >
          {function AccessLevelForm ({
            values,
            dirty,
            isSubmitting,
            setFieldValue,
            handleSubmit
          }) {
            const handleViewPermissionChanged = (value, permission, index) => {
              const isChild = permission.area_code.includes ('.')
              const isParent = !isChild

              setFieldValue (`permissions[${index}].selected`, value)

              if (value === false) {
                setFieldValue (`permissions[${index}].allow_edit`, false)
                setFieldValue (`permissions[${index}].allow_delete`, false)
              }
            
              if (isParent) {
                const updatedPermissions = values.permissions.map ((p, i) => {
                  if (p.area_code !== permission.area_code && p.area_code.startsWith (permission.area_code + '.')) {
                    return value === false 
                      ? { ...p, selected: false, allow_edit: false, allow_delete: false }
                      : { ...p, selected: true }
                  }
                  if (i === index) {
                    return value === false
                      ? { ...p, selected: false, allow_edit: false, allow_delete: false }
                      : { ...p, selected: true }
                  }
                  return p
                })
                setFieldValue ('permissions', updatedPermissions)
                return
              }

              if (isChild && value === false) {
                const parentAreaCode = permission.area_code.split ('.')[0]
                const siblings = values.permissions.filter (p => (
                  p.area_code.startsWith (parentAreaCode + '.') && p.area_code !== permission.area_code
                ))
                const allSiblingsUnselected = siblings.every (sib => !sib.selected)
                const parentIndex = values.permissions.findIndex (p => p.area_code === parentAreaCode)

                if (allSiblingsUnselected && parentIndex !== -1) {
                  const updatedPermissions = values.permissions.map ((p, i) => {
                    if (i === parentIndex) return { ...p, selected: false, allow_edit: false, allow_delete: false }
                    if (i === index) return { ...p, selected: false, allow_edit: false, allow_delete: false }
                    return p
                  })
                  setFieldValue ('permissions', updatedPermissions)
                  return
                }
                setFieldValue (`permissions[${index}].selected`, false)
              }

              if (isChild && value === true) {
                const parentAreaCode = permission.area_code.split ('.')[0]
                const parentIndex = values.permissions.findIndex (p => p.area_code === parentAreaCode)

                if (parentIndex !== -1 && !values.permissions[parentIndex].selected) {
                  const updatedPermissions = values.permissions.map ((p, i) => {
                    if (i === parentIndex) return { ...p, selected: true }
                    if (i === index) return { ...p, selected: true }
                    return p
                  })
                  setFieldValue ('permissions', updatedPermissions)
                  return
                }
                setFieldValue (`permissions[${index}].selected`, true)
              }
            }

            const handlePermissionChanged = (field, value, permission, index) => {
              const isChild = permission.area_code.includes ('.')
              const isParent = !isChild

              setFieldValue (`permissions[${index}].${field}`, value)

              if (value === true && !values.permissions[index].selected) {
                setFieldValue (`permissions[${index}].selected`, true)

                if (isChild) {
                  const parentAreaCode = permission.area_code.split ('.')[0]
                  const parentIndex = values.permissions.findIndex (p => p.area_code === parentAreaCode)
                  if (parentIndex !== -1 && !values.permissions[parentIndex].selected) {
                    setFieldValue (`permissions[${parentIndex}].selected`, true)
                  }
                }
              }

              if (isParent) {
                const updatedPermissions = values.permissions.map ((p, i) => {
                  if (i === index) {
                    return value === true
                      ? { ...p, [field]: true, selected: true }
                      : { ...p, [field]: false }
                  }
                  if (p.area_code.startsWith (permission.area_code + '.')) {
                    return value === true
                      ? { ...p, [field]: true, selected: true }
                      : { ...p, [field]: false }
                  }
                  return p
                })
                setFieldValue ('permissions', updatedPermissions)
              }

              if (isChild && value === false) {
                const parentAreaCode = permission.area_code.split ('.')[0]
                const siblings = values.permissions.filter (
                  p => p.area_code.startsWith(parentAreaCode + '.') && p.area_code !== permission.area_code
                )
                const allSiblingsUnselected = siblings.every (sib => !sib[field])
                const parentIndex = values.permissions.findIndex (p => p.area_code === parentAreaCode)

                if (allSiblingsUnselected && parentIndex !== -1) {
                  setFieldValue (`permissions[${parentIndex}].${field}`, false)
                }
              }

              if (isChild && value === true) {
                const parentAreaCode = permission.area_code.split ('.')[0]
                const parentIndex = values.permissions.findIndex (p => p.area_code === parentAreaCode)
                if (parentIndex !== -1 && !values.permissions[parentIndex][field]) {
                  setFieldValue (`permissions[${parentIndex}].${field}`, true)
                }
              }
            }

            const handleEditPermissionChanged = (value, permission, index) => {
              handlePermissionChanged ('allow_edit', value, permission, index)
            }

            const handleDeletePermissionChanged = (value, permission, index) => {
              handlePermissionChanged ('allow_delete', value, permission, index)
            }

            return (
              <>
                <DialogBody>
                  <Form onSubmit={handleSubmit} onKeyDown={submitOnEnter(toggleConfirmDialog, isSubmitting)}>
                    <SecondaryButton
                      label='Importar permissões'
                      leftIcon={<FaFileImport size={14} color='#FFC100' style={{marginBottom: 1}}/>}
                      onClick={toggleImportPermissionsModal}
                      width={200}
                      style={{alignSelf: 'center', marginBottom: 15}}
                    />
                    <CheckRow style={{paddingInline: 10}}>
                      <SubTitle>Área</SubTitle>
                      <SubTitle>Visualizar</SubTitle>
                      <SubTitle>Editar</SubTitle>
                      <SubTitle>Excluir</SubTitle>
                    </CheckRow>
                    <PermissionsContainer>
                      {values.permissions.map ((permission, index) => {
                        const isChild = permission.area_code.includes ('.')

                        return (
                          <CheckRow key={permission.area_code} isChild={isChild} index={index}>
                            <Label
                              htmlFor={`permissions.${index}.selected`}
                              style={{fontWeight: isChild ? 200 : 500}}
                            >
                              {permission.area_name}
                            </Label>
                            <CheckBox
                              id={`permissions.${index}.selected`}
                              value={permission.selected}
                              onValueChange={value => handleViewPermissionChanged (value, permission, index)}
                            />
                            {/* Só "Visualizar" vale nesta área (ver `isViewOnlyArea`): no lugar de editar e excluir, espaço vazio */}
                            {isViewOnlyArea(permission.area_code) ? (
                              <>
                                <span/>
                                <span/>
                              </>
                            ) : (
                              <>
                                <CheckBox
                                  id={`permissions.${index}.allow_edit`}
                                  value={permission.allow_edit}
                                  onValueChange={value => handleEditPermissionChanged (value, permission, index)}
                                />
                                <CheckBox
                                  id={`permissions.${index}.allow_delete`}
                                  value={permission.allow_delete}
                                  onValueChange={value => handleDeletePermissionChanged (value, permission, index)}
                                />
                              </>
                            )}
                          </CheckRow>
                        )}
                      )}
                    </PermissionsContainer>
                    {errorMessage && (
                      <ErrorContainer>
                        <p>{errorMessage}</p>
                      </ErrorContainer>
                    )}
                    <ImportPermissionsModal
                      visible={showImportPermissionsModal}
                      accessLevels={accessLevels}
                      onConfirm={importedPermissions => {
                        setFieldValue ('permissions', permissions.map (permission => {
                          const existingPermission = importedPermissions.find (p => p.id === permission.id)
                          return existingPermission
                            ? { ...existingPermission, selected: true }
                            : { ...permission, selected: false }
                        }))
                      }}
                      onClose={toggleImportPermissionsModal}
                    />
                    <AlertDialog
                      visible={showConfirmDialog}
                      title='Atenção!'
                      message='Deseja realmente alterar as permissões deste(a) profissional?'
                      confirmText='Sim'
                      cancelText='Não'
                      onConfirm={handleSubmit}
                      onClose={toggleConfirmDialog}
                    />
                  </Form>
                </DialogBody>
                {dirty && (
                  <DialogFooter>
                    <MainButton
                      type='button'
                      label='Salvar alterações'
                      onClick={toggleConfirmDialog}
                      disabled={isSubmitting}
                      loading={isSubmitting}
                      width={160}
                      style={{alignSelf: 'center'}}
                    />
                  </DialogFooter>
                )}
              </>
            )
          }}
        </Formik>
      </DialogContent>
    </Dialog>
  )
}
